import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Input, Modal, Space, Switch, Table, Tag } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { LayoutContext } from '../../contexts';
import { usePricingTiers } from '../../hooks/useMenuQuery';
import { useFetch } from '../../hooks';
import { URLS } from '../../constants';

function PricingTiers() {
  const navigate = useNavigate();
  const { setHeaderComponent } = useContext(LayoutContext);
  const { data: tiersData, isLoading, refetch } = usePricingTiers();
  const { doFetch } = useFetch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    setHeaderComponent(<b>Pricing Tiers</b>);
    return () => setHeaderComponent(null);
  }, [setHeaderComponent]);

  const tiers = useMemo(() => (Array.isArray(tiersData) ? tiersData : []), [tiersData]);

  const openCreateModal = () => {
    setEditingTier(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setIsModalOpen(true);
  };

  const openEditModal = tier => {
    setEditingTier(tier);
    form.setFieldsValue({
      name: tier.name,
      code: tier.code,
      is_active: !!tier.is_active,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTier(null);
    form.resetFields();
  };

  const submitModal = () => {
    form.validateFields().then(values => {
      const isEdit = !!editingTier?.id;
      doFetch({
        url: isEdit ? URLS.pricingTier({ tierId: editingTier.id }) : URLS.pricingTiers(),
        method: isEdit ? 'PATCH' : 'POST',
        params: values,
        onSuccess: () => {
          closeModal();
          refetch();
        },
        successMessage: isEdit ? 'Tier updated successfully' : 'Tier created successfully',
        showSuccessNotification: true,
      });
    });
  };

  const deactivateTier = tier => {
    doFetch({
      url: URLS.pricingTier({ tierId: tier.id }),
      method: 'DELETE',
      onSuccess: () => refetch(),
      successMessage: 'Tier deactivated successfully',
      showSuccessNotification: true,
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Button type="link" onClick={() => navigate(`/pricing-tiers/${record.id}`)} style={{ padding: 0 }}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: code => <code>{code}</code>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: isActive => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEditModal(record)}>
            Edit
          </Button>
          <Button
            danger
            size="small"
            disabled={!record.is_active}
            onClick={() => deactivateTier(record)}
          >
            Deactivate
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Pricing Tiers"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            New Tier
          </Button>
        }
      >
        <Table
          rowKey="id"
          dataSource={tiers}
          columns={columns}
          loading={isLoading}
          pagination={{ pageSize: 15 }}
        />
      </Card>

      <Modal
        title={editingTier ? 'Update Pricing Tier' : 'Create Pricing Tier'}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={submitModal}
        okText={editingTier ? 'Save Changes' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter tier name' }]}
          >
            <Input placeholder="B2B Gold" />
          </Form.Item>
          <Form.Item
            label="Code"
            name="code"
            rules={[{ required: true, message: 'Please enter tier code' }]}
          >
            <Input placeholder="b2b_gold" />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PricingTiers;
